import { PageCanvas, PageHeader } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  newLocationJobPath,
  newLocationRequestPath,
  locationJobPath,
  locationVehicleHistoryPath,
  editLocationVehiclePath,
  locationCustomerPath,
  locationCustomersPath,
  newLocationVehicleOwnershipPath,
} from "@/routes";
import { dateTime } from "@/pages/jobs/types";
import type {
  VehicleProfile,
  VehicleOwner,
  VehicleProfileAction,
} from "./types";

export default function VehicleShow({
  vehicle,
  owner,
  history,
  service_history,
}: {
  vehicle: VehicleProfile;
  owner: VehicleOwner | null;
  history: VehicleProfileAction[];
  service_history: {
    jobs: {
      id: string;
      starts_at: string;
      time_zone: string;
      work_state: string;
      services: { name: string; quantity: number }[];
    }[];
    page: number;
    pages: number;
  };
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = `${vehicle.make} ${vehicle.model}`;
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("customers.title"),
          href: locationCustomersPath(locationId),
        },
        ...(owner
          ? [
              {
                title: owner.name,
                href: locationCustomerPath(locationId, owner.id),
              },
            ]
          : []),
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <PageHeader
          title={title}
          description={owner?.name}
          actions={
            owner && (
              <>
                <Button
                  render={
                    <Link
                      href={newLocationJobPath(locationId, {
                        vehicle_id: vehicle.id,
                        composition: {
                          origin: "vehicle",
                          origin_id: vehicle.id,
                        },
                      })}
                    />
                  }
                >
                  {t("jobs.create")}
                </Button>
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={newLocationRequestPath(locationId, {
                        vehicle_id: vehicle.id,
                      })}
                    />
                  }
                >
                  {t("requests.add")}
                </Button>
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={editLocationVehiclePath(locationId, vehicle.id)}
                    />
                  }
                >
                  {t("vehicles.edit")}
                </Button>
              </>
            )
          }
        />
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)]">
          <div className="min-w-0 space-y-6">
            <section
              className="space-y-4 rounded-lg border p-4"
              aria-labelledby="vehicle-service-history"
            >
              <h2
                id="vehicle-service-history"
                className="text-lg font-semibold"
              >
                {t("vehicle_workspace.service_history")}
              </h2>
              {!service_history.jobs.length && (
                <p className="text-muted-foreground text-sm">
                  {t("vehicle_workspace.no_visits")}
                </p>
              )}
              {service_history.jobs.map((job) => (
                <article key={job.id} className="space-y-2 border-b pb-4">
                  <div className="flex flex-wrap justify-between gap-3">
                    <Link
                      className="font-medium underline"
                      href={locationJobPath(locationId, job.id)}
                    >
                      {dateTime(job.starts_at, job.time_zone, i18n.locale)}
                    </Link>
                    <span className="text-sm">
                      {t(`job_board.stages.${job.work_state}`)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {job.services
                      .map((service) => `${service.quantity} × ${service.name}`)
                      .join(" · ")}
                  </p>
                </article>
              ))}
              <nav
                className="flex flex-wrap gap-3"
                aria-label={t("vehicle_workspace.service_history")}
              >
                {service_history.page > 1 && (
                  <Link
                    className="underline"
                    href={`?visits_page=${service_history.page - 1}`}
                  >
                    {t("customers.previous")}
                  </Link>
                )}
                {service_history.page < service_history.pages && (
                  <Link
                    className="underline"
                    href={`?visits_page=${service_history.page + 1}`}
                  >
                    {t("customers.next")}
                  </Link>
                )}
              </nav>
            </section>
            <Button
              variant="outline"
              render={
                <Link
                  href={locationVehicleHistoryPath(locationId, vehicle.id)}
                />
              }
            >
              {t("vehicle_reports.open")}
            </Button>
          </div>
          <aside className="min-w-0 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("vehicles.details")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <dl className="space-y-4">
                  <div>
                    <dt className="text-muted-foreground text-sm">
                      {t("vehicles.owner")}
                    </dt>
                    <dd>
                      {owner ? (
                        <Link
                          className="underline"
                          href={locationCustomerPath(locationId, owner.id)}
                        >
                          {owner.name}
                        </Link>
                      ) : (
                        t("vehicles.owner_private")
                      )}
                    </dd>
                  </div>
                  {(
                    [
                      "fabrication_year",
                      "colour",
                      "registration_number",
                      "vin",
                    ] as const
                  ).map((field) => (
                    <div key={field}>
                      <dt className="text-muted-foreground text-sm">
                        {t(`vehicles.${field}`)}
                      </dt>
                      <dd className="break-words">
                        {vehicle[field] ?? t("customers.not_supplied")}
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="grid grid-cols-2 gap-3">
                  {vehicle.photos.map((photo) => (
                    <a key={photo.id} href={photo.path}>
                      <img
                        className="aspect-square w-full rounded-lg border object-contain"
                        src={photo.path}
                        alt={photo.name}
                      />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
            {owner && (
              <Button
                variant="outline"
                render={
                  <Link
                    href={newLocationVehicleOwnershipPath(
                      locationId,
                      vehicle.id,
                    )}
                  />
                }
              >
                {t("vehicles.transfer")}
              </Button>
            )}
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("vehicles.history")}</h2>
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  {t("vehicles.history_help")}
                </p>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  {history.map((action) => (
                    <li key={action.id} className="rounded-lg border p-3">
                      <p className="font-medium">
                        {t(`vehicles.actions.${action.action}`)}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {new Intl.DateTimeFormat(i18n.locale, {
                          dateStyle: "medium",
                          timeStyle: "short",
                          timeZone: i18n.time_zone,
                        }).format(new Date(action.occurred_at))}
                      </p>
                      <p className="break-words">
                        {[
                          action.details.make,
                          action.details.model,
                          action.details.fabrication_year,
                          action.details.colour,
                          action.details.registration_number,
                          action.details.vin,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </aside>
        </div>
      </PageCanvas>
    </AppLayout>
  );
}
